import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Photo } from '../../types/types';
import { useAnniversary } from '../../context/AnniversaryContext';

const PhotoGallery: React.FC = () => {
  const { data } = useAnniversary();
  const [currentPhoto, setCurrentPhoto] = useState<Photo | null>(null);
  
  // Parallax scrolling effect
  const { scrollY } = useScroll();
  
  // Create transform values outside of callbacks
  // We'll create a fixed number of transform values with different speeds
  const y1 = useTransform(scrollY, [0, 2000], [0, 100]); // slow
  const y2 = useTransform(scrollY, [0, 2000], [0, 200]); // medium
  const y3 = useTransform(scrollY, [0, 2000], [0, 300]); // fast
  const y4 = useTransform(scrollY, [0, 2000], [0, 400]); // faster
  const y5 = useTransform(scrollY, [0, 2000], [0, 500]); // fastest
  
  // Function to get parallax y value based on index
  const getParallaxY = (index: number) => {
    // Cycle through the 5 different speeds
    switch (index % 5) {
      case 0: return y1;
      case 1: return y2;
      case 2: return y3;
      case 3: return y4;
      case 4: return y5;
      default: return y1;
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Open photo in lightbox
  const openLightbox = (photo: Photo) => {
    setCurrentPhoto(photo);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when lightbox is open
  };
  
  // Close lightbox
  const closeLightbox = () => {
    setCurrentPhoto(null);
    document.body.style.overflow = ''; // Restore scrolling
  };
  
  // Navigate to previous photo
  const prevPhoto = useCallback(() => {
    if (!currentPhoto) return;
    
    const currentIndex = data.photos.findIndex(photo => photo.id === currentPhoto.id);
    const prevIndex = currentIndex <= 0 ? data.photos.length - 1 : currentIndex - 1;
    setCurrentPhoto(data.photos[prevIndex]);
  }, [currentPhoto, data.photos]);
  
  // Navigate to next photo
  const nextPhoto = useCallback(() => {
    if (!currentPhoto) return;
    
    const currentIndex = data.photos.findIndex(photo => photo.id === currentPhoto.id);
    const nextIndex = currentIndex >= data.photos.length - 1 ? 0 : currentIndex + 1;
    setCurrentPhoto(data.photos[nextIndex]);
  }, [currentPhoto, data.photos]);
  
  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!currentPhoto) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          prevPhoto();
          break;
        case 'ArrowRight':
          nextPhoto();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPhoto, prevPhoto, nextPhoto]);

  // Random position generator for collage effect
  const getRandomPosition = (index: number) => {
    // Use index to create some deterministic randomness
    const seed = index * 237 % 100;
    
    // Create different position clusters
    const positions = [
      { top: `${5 + seed % 30}%`, left: `${5 + (seed * 3) % 40}%`, rotate: -5 + (seed % 10) },
      { top: `${30 + seed % 30}%`, left: `${50 + (seed * 2) % 45}%`, rotate: 3 - (seed % 8) },
      { top: `${60 + seed % 30}%`, left: `${10 + (seed * 4) % 50}%`, rotate: 7 - (seed % 15) },
      { top: `${70 + seed % 20}%`, left: `${60 + (seed * 5) % 35}%`, rotate: -10 + (seed % 20) },
      { top: `${10 + seed % 40}%`, left: `${75 + (seed * 2) % 20}%`, rotate: 5 - (seed % 12) },
    ];
    
    // Choose a position based on index
    return positions[index % positions.length];
  };
  
  // Generate random size for collage effect
  const getRandomSize = (index: number) => {
    const sizes = ['small', 'medium', 'large', 'extra-large'];
    // Use index to choose size more deterministically
    return sizes[index % sizes.length];
  };
  
  return (
    <CollageSection id="gallery">
      <CollageOverlay>
        <CollageTitleContainer>
          <CollageTitle
            as={motion.h1}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Our Photobook
          </CollageTitle>
          <CollageSubtitle
            as={motion.p}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Scroll to explore our memories
          </CollageSubtitle>
        </CollageTitleContainer>
      </CollageOverlay>
      <CollageContainer>
        <CollageBackground>
          {data.photos.map((photo, index) => {
            const position = getRandomPosition(index);
            const size = getRandomSize(index);
            
            return (
              <CollagePhoto
                key={photo.id}
                as={motion.div}
                sizeClass={size}
                style={{
                  top: position.top,
                  left: position.left,
                  rotate: `${position.rotate}deg`,
                  y: getParallaxY(index)
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ 
                  opacity: 1, 
                  scale: 1,
                  transition: { 
                    duration: 0.8, 
                    delay: index * 0.1 % 0.5
                  } 
                }}
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ 
                  scale: 1.05, 
                  zIndex: 10,
                  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)',
                  transition: { duration: 0.3 } 
                }}
                onClick={() => openLightbox(photo)}
              >
                <PhotoImage src={photo.path} alt={photo.caption} />
                <PhotoInfo>
                  <h3>{photo.caption}</h3>
                  <p>{formatDate(photo.date)}</p>
                </PhotoInfo>
              </CollagePhoto>
            );
          })}
        </CollageBackground>
      </CollageContainer>
      
      {/* Lightbox */}
      <AnimatePresence>
        {currentPhoto && (
          <Lightbox
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <LightboxContent
              as={motion.div}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking content
            >
              <LightboxClose onClick={closeLightbox}>&times;</LightboxClose>
              <LightboxNavButton className="prev" onClick={prevPhoto}>&lt;</LightboxNavButton>
              <LightboxNavButton className="next" onClick={nextPhoto}>&gt;</LightboxNavButton>
              
              <LightboxImageContainer>
                <LightboxImage src={currentPhoto.path} alt={currentPhoto.caption} />
              </LightboxImageContainer>
              
              <LightboxCaption>
                <h3>{currentPhoto.caption}</h3>
                <p>{formatDate(currentPhoto.date)}</p>
                {currentPhoto.location && <p className="location">{currentPhoto.location}</p>}
              </LightboxCaption>
            </LightboxContent>
          </Lightbox>
        )}
      </AnimatePresence>
    </CollageSection>
  );
};

const CollageSection = styled.section`
  position: relative;
  width: 100%;
  min-height: 200vh;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const CollageOverlay = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 5;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0) 70%,
    rgba(0, 0, 0, 0.6) 100%
  );
`;

const CollageTitleContainer = styled.div`
  text-align: center;
  margin-bottom: 30vh;
`;

const CollageTitle = styled(motion.h1)`
  font-size: 5rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 3.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const CollageSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  color: white;
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const CollageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 200vh; /* Tall container to allow for scrolling */
`;

const CollageBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CollagePhoto = styled(motion.div)<{ sizeClass: string }>`
  position: absolute;
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 10px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  /* Size variations */
  ${props => {
    switch(props.sizeClass) {
      case 'small':
        return `
          width: 200px;
          height: 150px;
          z-index: 1;
        `;
      case 'medium':
        return `
          width: 250px;
          height: 200px;
          z-index: 2;
        `;
      case 'large':
        return `
          width: 300px;
          height: 250px;
          z-index: 3;
        `;
      case 'extra-large':
        return `
          width: 350px;
          height: 300px;
          z-index: 4;
        `;
      default:
        return `
          width: 250px;
          height: 200px;
          z-index: 2;
        `;
    }
  }}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    ${props => {
      switch(props.sizeClass) {
        case 'small':
          return `width: 150px; height: 120px;`;
        case 'medium':
          return `width: 180px; height: 150px;`;
        case 'large':
          return `width: 220px; height: 180px;`;
        case 'extra-large':
          return `width: 250px; height: 200px;`;
        default:
          return `width: 180px; height: 150px;`;
      }
    }}
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${props => {
      switch(props.sizeClass) {
        case 'small':
          return `width: 120px; height: 100px;`;
        case 'medium':
          return `width: 150px; height: 120px;`;
        case 'large':
          return `width: 180px; height: 150px;`;
        case 'extra-large':
          return `width: 200px; height: 170px;`;
        default:
          return `width: 150px; height: 120px;`;
      }
    }}
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const PhotoInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 10px;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  
  ${CollagePhoto}:hover & {
    transform: translateY(0);
  }
  
  h3 {
    font-size: 0.9rem;
    margin-bottom: 5px;
  }
  
  p {
    font-size: 0.8rem;
    opacity: 0.8;
  }
`;

const Lightbox = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const LightboxContent = styled(motion.div)`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
`;

const LightboxImageContainer = styled.div`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const LightboxImage = styled.img`
  width: 100%;
  height: 70vh;
  object-fit: contain;
  background-color: rgba(0, 0, 0, 0.8);
`;

const LightboxCaption = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
  
  p {
    margin: 0;
    opacity: 0.8;
  }
  
  .location {
    margin-top: ${({ theme }) => theme.spacing.xs};
    font-style: italic;
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.light};
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
`;

const LightboxNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.light};
  background: rgba(0, 0, 0, 0.3);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  
  &.prev {
    left: -70px;
  }
  
  &.next {
    right: -70px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    &.prev {
      left: -20px;
    }
    
    &.next {
      right: -20px;
    }
  }
`;

export default PhotoGallery; 